/* The video queue (section 2).
 *
 * Every number on this screen is derived from the rows, live (8.2.1). There is
 * no counter anywhere — not in the database and not in a useState. A stored
 * count is a second copy of a fact, and the second copy is the one that goes
 * stale while nobody is looking.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { api } from "../api/client";
import type { VideoAccuracy, VideoRow } from "../api/types";
import { pct } from "../lib/accuracy";
import { duration, matchesQuery, money, pageNumbers, queueStats } from "../lib/format";
import { displayName, featuredRank } from "../lib/featured";
import { batchRank } from "../lib/batches";
import { categoryLabel } from "../lib/categoryLabel";
import { ChecklistDrawer } from "../components/Drawers";
import { ActiveFilterChips, applyFilters, EMPTY_FILTERS, FilterPanel }
  from "../components/FilterPanel";
import type { Filters } from "../components/FilterPanel";
import { currentName, currentRole, signOut } from "../lib/auth";
import { toVideo } from "../lib/queuereturn";
import { Button, Empty, Eyebrow, Spinner, useToast } from "../components/ui";

const RATE_PER_MINUTE = 6; // job configuration; display only (2.2.2, 8.5.1)

/** 2.3.4: the pill the annotator reads. Derived from stage+status rather than
 *  stored, so it cannot disagree with the workflow.
 *
 *  THE VOCABULARY FOLLOWS Video_Annotation_Tool_v1.6 where it has an opinion:
 *  Submitted to QA · Ready for review (nothing reviewed yet) · In progress
 *  (partly reviewed). Those three describe the ANNOTATOR'S work, and "In
 *  progress" means specifically "you have started this one" — which is why the
 *  row also offers Continue rather than Start review.
 *
 *  The prototype has no state for a video the AI has not reached, because every
 *  video in the prototype already had annotations. A real 190-video corpus has
 *  161 of them, so the state exists and needs a name. "Not processed yet" was
 *  the wrong one — it reads as a gap in the delivery rather than a queue
 *  position. "Queued" says the same thing as a plan.
 *
 *  It is deliberately NOT "In progress", which was the literal request from
 *  testing: that label already means "the annotator has started this video",
 *  and one word for two states in one column is worse than the wording it
 *  replaced — the reader cannot tell which rows they have touched. The AI's own
 *  working state is "Processing…" for the same reason. */
function pill(j: VideoRow): { text: string; tone: string } {
  if (j.job_id === null)
    return { text: "Queued", tone: "bg-ground text-muted ring-line" };
  if (j.current_stage === "AI")
    return j.current_status === "COMPLETED"
      ? { text: "Ready for review", tone: "bg-tealbg text-teal7 ring-teal1" }
      : { text: "Processing…", tone: "bg-ground text-muted ring-line" };
  if (j.current_stage === "ANNOTATOR")
    // COMPLETED IS THE HANDOFF, NOT A STAGE CHANGE. submit_annotation records
    // ANNOTATOR/COMPLETED and the job waits there until QA claims it — the
    // stage only becomes QA when someone on that side opens it. Reading the
    // stage alone made a submitted video keep saying "In progress" with a
    // Continue button, which is what testing reported: mfg_assembling_002 was
    // submitted and still looked untouched.
    return j.current_status === "COMPLETED"
      ? { text: "✓ Submitted to QA", tone: "bg-verdict-approved-bg text-verdict-approved-ink ring-verdict-approved-line" }
      : { text: "In progress", tone: "bg-verdict-flagged-bg text-verdict-flagged-ink ring-verdict-flagged-line" };
  if (j.current_stage === "QA")
    return j.current_status === "REJECTED"
      ? { text: "Sent back by QA", tone: "bg-verdict-rejected-bg text-verdict-rejected-ink ring-verdict-rejected-line" }
      : { text: "✓ Submitted to QA", tone: "bg-verdict-approved-bg text-verdict-approved-ink ring-verdict-approved-line" };
  return { text: "✓ Delivered", tone: "bg-verdict-approved-bg text-verdict-approved-ink ring-verdict-approved-line" };
}

/** THE ROW'S THUMBNAIL — a stored picture, or the video if there isn't one.
 *
 *  Testing, 21 Sep, point 4: "the thumbnails images take time to load."
 *
 *  It used to be a `<video preload="metadata" src="…#t=1">` and nothing else:
 *  the browser opened a real video file, parsed its container, seeked to one
 *  second and decoded a frame — ten times at once, to paint a 64x40 picture.
 *  Even after the faststart remux that is about a megabyte per row, because the
 *  floor is the video's index and these carry an entry per frame.
 *
 *  `poster_url` is a ~20 KB JPEG stored beside the video (tools/make_posters.py
 *  builds them, media.poster_url_for signs them). Fifty times less, and no
 *  video decoding at all.
 *
 *  THE FALLBACK IS THE POINT. A poster is derived, never looked up, so the API
 *  hands out a URL whether or not the object exists — a deployment where the
 *  tool has not been run, or a video added since it last ran, gets a 404. That
 *  fires `onError`, and the row quietly goes back to the `<video>` it used
 *  before. Nothing breaks and nothing shows a broken-image icon; the queue is
 *  just as slow as it used to be until the posters are built.
 *
 *  `loading="lazy"` because ten rows fit the viewport and the rest are below
 *  the fold. `decoding="async"` so painting the table never waits on a JPEG.
 *
 *  muted + playsInline + no controls on the fallback: it is a picture, not a
 *  player. Clicking it does nothing; the row's button is how you open a video. */
function Thumb({ poster, media }: { poster: string | null; media: string | null }) {
  // One flag, one direction: posters fail permanently (the object is absent),
  // so there is nothing to retry and no reason to ever go back.
  const [posterFailed, setPosterFailed] = useState(false);
  const showPoster = !!poster && !posterFailed;
  return (
    // 27 Oct 2026 (manager's review): rows 20% taller. Row = thumbnail +
    // cell padding: was 40 + 2x10 = 60 px, now 48 + 2x12 = 72 px. The box keeps
    // its 64:40 shape (now 76.8:48) and object-cover crops rather than
    // stretches, so the picture is larger but not distorted.
    <div className="h-12 w-[4.8rem] shrink-0 overflow-hidden rounded-chip
      bg-navy9 ring-1 ring-line">
      {showPoster ? (
        <img
          src={poster}
          className="h-full w-full object-cover"
          loading="lazy"
          decoding="async"
          onError={() => setPosterFailed(true)}
          alt=""
          aria-hidden="true"
        />
      ) : media ? (
        <video
          src={`${media}#t=1`}
          className="h-full w-full object-cover"
          preload="metadata"
          muted
          playsInline
          tabIndex={-1}
          aria-hidden="true"
        />
      ) : null}
    </div>
  );
}

/** 2.3.3: the row's action follows its state. */
function action(j: VideoRow): { label: string; to: string | null } {
  // Nothing to open: there is no job, so there is no annotation to review.
  if (j.job_id === null) return { label: "—", to: null };
  if (j.current_stage === "AI")
    return j.current_status === "COMPLETED"
      ? { label: "Start review", to: `/job/${j.job_id}` }
      : { label: "Available soon", to: null };
  if (j.current_stage === "ANNOTATOR")
    // Submitted work is read-only to the annotator — View, not Continue.
    return j.current_status === "COMPLETED"
      ? { label: "View", to: `/job/${j.job_id}` }
      : { label: "Continue", to: `/job/${j.job_id}` };
  if (j.current_stage === "QA" && j.current_status !== "APPROVED")
    return { label: "QA review", to: `/qa/${j.job_id}` };
  return { label: "View", to: `/job/${j.job_id}` };
}

export function Queue() {
  const [jobs, setJobs] = useState<VideoRow[] | null>(null);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [guidelines, setGuidelines] = useState(false);
  const role = currentRole();
  /* QA ONLY: the Accuracy page's numbers per video, from the SAME endpoint, so
     the queue can never disagree with /accuracy. Annotators do not see them —
     they are about the pipeline, not about the work in front of them. */
  const [acc, setAcc] = useState<Map<number, VideoAccuracy> | null>(null);
  useEffect(() => {
    if (role !== "qa") return;
    let live = true;
    api.accuracy().then((r) => {
      if (live) setAcc(new Map(r.videos.map((v) => [v.video_id, v])));
    }).catch(() => { if (live) setAcc(new Map()); });
    return () => { live = false; };
  }, [role]);
  const qa = role === "qa";
  const toast = useToast();
  const navigate = useNavigate();
  // Read for one reason: to hand the queue's own query string to every row's
  // link, so the video screen can find its way back here. See lib/queuereturn.
  const location = useLocation();

  useEffect(() => {
    api.videos()
      .then(setJobs)
      .catch((e) => { setJobs([]); toast(e.message, "error"); });
  }, [toast]);

  /* RESET ALL FOR A DEMO (26 Oct 2026, manager's point 3). Two clicks: the
     first arms it for five seconds and says what it will do, the second does
     it — no browser dialog. Statuses only: AI-approved sub-tasks stay done,
     every other sub-task is pending again, every video "Ready for review". */
  const [resetArmed, setResetArmed] = useState(false);
  const [resetting, setResetting] = useState(false);
  useEffect(() => {
    if (!resetArmed) return;
    const t = window.setTimeout(() => setResetArmed(false), 5000);
    return () => window.clearTimeout(t);
  }, [resetArmed]);
  const resetAll = () => {
    if (!resetArmed) { setResetArmed(true); return; }
    setResetArmed(false);
    setResetting(true);
    api.resetAllForDemo()
      .then((r) => {
        toast(`Reset ${r.videos} videos: ${r.subtasks_reopened} sub-tasks are pending again; ` +
              "the AI-approved ones stay done.");
        return api.videos().then(setJobs);
      })
      .catch((e) => toast(e.message, "error"))
      .finally(() => setResetting(false));
  };

  /** The rows the queue lists: annotated videos only.
   *
   *  Confirmed by Bala and Shruti. A video the AI has not reached is nothing an
   *  annotator can act on — its row had no counts, no progress and no button,
   *  so 184 of the 190 rows were unreachable and the six with work in them were
   *  scattered among them.
   *
   *  THE WHOLE LIBRARY IS STILL REPORTED, in the stats strip above: "Videos in
   *  library 190" and "Queued 184" are the corpus we hold and the part still
   *  waiting. Dropping those too would turn a queue that is deliberately short
   *  into a library that looks nearly empty, and nothing on screen would
   *  explain where the other 184 went. */
  /** QA SEES A DIFFERENT SLICE, NOT DIFFERENT PERMISSIONS. Everything QA can
   *  do, the annotator can too — what changes is which videos are theirs to
   *  look at. An annotator's queue is work outstanding; QA's is work the
   *  annotator has already approved or rejected and submitted. Filtering here
   *  rather than gating routes keeps one set of screens and one set of rules. */
  const listed = useMemo(() => {
    const withJobs = (jobs ?? []).filter((j) => j.job_id !== null);
    if (role !== "qa") return withJobs;
    return withJobs.filter((j) =>
      (j.current_stage === "ANNOTATOR" && j.current_status === "COMPLETED")
      || j.current_stage === "QA" || j.current_stage === "DELIVERED");
  }, [jobs, role]);

  // Counted from `listed`, NOT from the whole corpus. Chips built from every
  // video would offer "Warehouse Packing · Sorting 15" and then show an empty
  // table, because none of those fifteen are annotated yet.
  /** 2.3.5: the order testing asked for — In progress, Ready for review,
   *  Rejected, Approved. Work you are in the middle of first, work you have
   *  finished with last; a queue sorted by name buries the row you left. */
  const STATUS_ORDER = (j: VideoRow): number => {
    if (j.current_stage === "ANNOTATOR" && j.current_status !== "COMPLETED") return 0;
    if (j.current_stage === "AI" && j.current_status === "COMPLETED") return 1;
    if (j.current_status === "REJECTED") return 2;
    if (j.current_status === "APPROVED" || j.current_stage === "DELIVERED") return 4;
    return 3;   // submitted to QA, awaiting a decision
  };

  const visible = useMemo(() => applyFilters(listed, filters)
    .filter((j) => matchesQuery(j, search))
    // FEATURED FIRST — a demo-only layer; see lib/featured.ts, and empty its
    // FEATURED array to remove it entirely. It is the first key on purpose:
    // the presentation videos must not drift off the top just because one
    // of them is half-reviewed.
    // THEN BY BATCH (lib/batches.ts, 13 Oct): the old 49 on pages 1–5, then
    // cut_720p, then Egocentric_Videos. Status and name order apply within a batch.
    .sort((a, b) => featuredRank(a.video_path) - featuredRank(b.video_path)
    || batchRank(a.video_path) - batchRank(b.video_path)
    || STATUS_ORDER(a) - STATUS_ORDER(b)
    || displayName(a.video_path).localeCompare(displayName(b.video_path))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listed, search, filters]);

  /* TEN PER PAGE, WHATEVER IS BEING LISTED.
   *
   * Testing, 16 Sep: "no matter whether we are showing all videos OR videos
   * after applying filter, we will show only 10 videos per page."
   *
   * So the slice is taken from `visible` — AFTER the role slice, the filters,
   * the search and the sort. Paginating before any of those would give pages of
   * different sizes depending on how many rows each one happened to drop, which
   * is the bug this replaces rather than a design.
   *
   * The page RESETS whenever the list changes underneath it. Filtering from 70
   * rows to 12 while sitting on page 5 would otherwise show an empty table and
   * no clue why — the rows are gone and so is the page they were on. */
  /* THE PAGE LIVES IN THE URL — testing, 21 Sep: "if I click back from the
     working screen I should return to the page I was on; currently I always
     land on the first page."

     It was `useState(1)`. Opening a video unmounts this component, so the page
     number simply ceased to exist; coming back mounted a fresh one at 1. On
     page 5 of a 49-video queue that is four clicks to get back to where you
     were, every single time.

     `useSearchParams` puts it in the address bar instead, which is the one
     piece of state the browser preserves across navigation for free. Back now
     restores the page, and a link to page 3 is shareable — which the old
     version could not express at all.

     `replace: true` on paging so the history stack does not fill with one entry
     per page click; Back should leave the queue, not walk back through the
     pages you flicked past. */
  const PER_PAGE = 10;
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get("page")) || 1);
  const setPage = useCallback((n: number) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      // Page 1 is the default, so it does not earn a query string.
      if (n <= 1) next.delete("page"); else next.set("page", String(n));
      return next;
    }, { replace: true });
  }, [setParams]);

  /* Still resets when the list changes underneath it. Filtering from 49 rows to
     12 while sitting on page 5 would otherwise show an empty table and no clue
     why — the rows are gone and so is the page they were on.

     ONLY WHEN THE LIST ACTUALLY CHANGES, which is not what it used to do.

     This read `if (page > 1) setPage(1)` with a comment claiming the guard
     stopped it firing on mount. It does the exact opposite: an effect always
     runs once after mounting, so arriving at /?page=3 ran it, saw 3 > 1, and
     reset to page 1 — silently erasing the restore the URL had just performed.
     Both halves of point 5 were therefore inert: the Back button dropped the
     query string, and anything that did arrive with one lost it a frame later.

     WHY A REMEMBERED VALUE AND NOT A FIRST-RUN FLAG. The obvious repair is a
     ref saying "skip the first run". It does not survive <StrictMode>, which
     deliberately invokes every effect twice in development: the first call
     flips the flag, the second sails through and resets the page. Verified in
     a browser — /?page=3 became / inside 300ms with the flag version in place.

     Comparing the DEPENDENCY VALUES is immune to that, because running twice
     with the same values is exactly the case it declines to act on. It also
     expresses the real condition rather than a proxy for it: reset the page
     when the list changed under it, not when the component happens to be new.
     Search, filters and role are what can shrink the list under a page. */
  const listKey = JSON.stringify([search, filters, role]);
  const lastListKey = useRef<string | null>(null);
  useEffect(() => {
    if (lastListKey.current === listKey) return;   // nothing changed
    const first = lastListKey.current === null;
    lastListKey.current = listKey;
    if (!first && page > 1) setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listKey]);

  const pageCount = Math.max(1, Math.ceil(visible.length / PER_PAGE));
  // Clamped, not trusted: deleting rows can strand the page number past the end.
  const current = Math.min(page, pageCount);
  const paged = useMemo(
    () => visible.slice((current - 1) * PER_PAGE, current * PER_PAGE),
    [visible, current],
  );

  const stats = useMemo(() => queueStats(jobs ?? [], RATE_PER_MINUTE), [jobs]);

  // The chip clamp and its More button went with the chips, and useRowClamp
  // with them. It measured how many chips fit in two rows; there are no chips.
  // Keeping it "for next time" is how a file accumulates code nobody can
  // safely delete later — git has it if a wrapping row ever needs clamping.
  if (!jobs) return <div className="p-8"><Spinner /></div>;

  return (
    <div className="h-full overflow-y-auto">
      {/* -------------------------------------------------------- header */}
      <header className="border-b border-line bg-card px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-3">
            <span className="text-sm font-semibold text-teal">Shaip Hub</span>
            <span className="text-xs text-muted">My jobs › Egocentric annotation</span>
          </div>
          <div className="flex items-center gap-3">
            {/* "Job active" is gone — it was a badge with no state behind it,
                on every screen regardless of whether anything was active. The
                person's NAME goes here, not their login: "crowd" is a
                credential, "Asha N." is who the row says reviewed the work. */}
            {/* THE ROLE SITS UNDER THE NAME, not after it on the same line.
                Testing asked for it there, and stacked is what the two things
                are: who you are, and what you are here as. Inline behind a "·"
                it read as more of the name — "Asha N. · Annotator" is one
                string at a glance, and the role is the half that changes what
                the screen below it contains. */}
            {/* QA only: the (a)/(b) report is about the pipeline, not the
                annotator's work, and has no place in their queue. */}
            {role === "qa" && (
              <Link to="/accuracy" className="rounded-chip bg-ground px-2.5 py-1 text-xs
                text-text2 border border-line hover:bg-line">Accuracy</Link>
            )}
            <span className="flex flex-col items-end leading-tight">
              <span className="text-xs text-text2">{currentName()}</span>
              <Eyebrow>{role === "qa" ? "QA" : "Annotator"}</Eyebrow>
            </span>
            <button
              onClick={() => { signOut(); navigate("/login", { replace: true }); }}
              className="rounded-chip bg-ground px-2.5 py-1 text-xs text-text2
                border border-line hover:bg-line"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      {/* max-w-7xl — testing, 18 Sep, asked for the landing page to be
          slightly wider. 1152px -> 1280px. Still capped rather than full-width:
          an eight-column table stretched across a 2560px monitor puts the
          Action button so far from the video name that they stop reading as the
          same row. */}
      <div className="mx-auto max-w-7xl px-6 py-6">
        {/* ------------------------------------------------------- band */}
        {/* The two buttons sat UNDER the paragraph, so the reference material
            was the last thing on the card and the eye reached it after the
            text it explains. Top right, on the title's line: reference actions
            belong at the edge, and the paragraph now ends the card instead of
            being interrupted. It also saves a row — `items-start` keeps them
            aligned to the heading rather than centred on the whole block. */}
        <section className="rounded-panel bg-card shadow-card p-5 border border-line">
          <div className="flex items-start justify-between gap-4">
            <h1 className="text-lg font-extrabold tracking-[-0.01em] text-ink">
              Egocentric video annotation
            </h1>
            {/* NO SHORTCUTS BUTTON HERE. Every key on that sheet acts on a
                sub-task, a playhead or the checklist, and this page has none of
                them — it was a list of thirteen things that do nothing where it
                was offered. It lives on the working screen, behind the ?. */}
            <div className="flex shrink-0 gap-2">
              <Button
                variant={resetArmed ? "danger" : "ghost"}
                onClick={resetAll}
                disabled={resetting}
                title="For a demo: every video back to 'Ready for review'. AI-approved sub-tasks stay done; the rest become pending. Label edits are kept."
              >
                {resetting ? "Resetting…" : resetArmed ? "Click again to reset ALL videos" : "Reset all for demo"}
              </Button>
              <Button onClick={() => setGuidelines(true)}>Customer guidelines</Button>
            </div>
          </div>
          {/* 27 Oct 2026 (manager's review): this exact text replaces the
              longer Video_Annotation_Tool_v1.6 paragraph. Keep it verbatim. */}
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted">
            AI has already split each video into tasks and sub-tasks and drafted
            every label — you review each sub-task, fix timing or labels,
            approve or reject, then submit. QA takes over from there.
          </p>
        </section>

        {/* ------------------------------------------------------ stats */}
        {/* FIVE, in the team's order:

              Ready for review · In progress · Submitted to QA ·
              Approved footage · Earned so far

            "Rejected video" is gone at testing's request. It counted videos QA
            had sent back, and QA sending a video back is a transition the
            workflow does not have yet — Shruti and Bala are still settling it —
            so the card could only ever read 0. A permanent zero beside four
            live numbers reads as "nothing has been rejected", which is a claim
            about the work rather than about a feature that does not exist.

            `queueStats` still computes rejectedVideos: the moment QA can send
            a video back, this card comes back with it, and deleting the
            derivation would take the definition with it.

            "In my queue" is deliberately not among them either — the prototype
            has it because it assumes several annotators, and there is one.
            Confirmed with Shruti. */}
        <section className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <Stat label="Ready for review" value={String(stats.ready)} />
          <Stat label="In progress" value={String(stats.inProgress)} />
          <Stat label="Submitted to QA" value={String(stats.submitted)} />
          <Stat label="Approved footage" value={String(stats.approvedVideos)}
            hint={`${stats.approvedMinutes.toFixed(1)} min`} />
          <Stat
            label="Earned so far"
            value={money(stats.earned)}
            hint={`at ${money(RATE_PER_MINUTE)}/min`}
          />
        </section>

        {/* ----------------------------------------------------- filters
            The Filter popover replaces the chip row. 27 sub-categories as chips
            took seven lines and pushed the table below the fold — the original
            complaint. Clamping them to two lines behind a More button treated
            the symptom; a flat list of every value stops being a filter once
            the corpus is longer than a page, and this one is 190 videos.

            The shape is the platform's existing one — names on the left, the
            selected filter's control on the right, Clear All / Cancel / Apply
            underneath — so anyone who has used another Shaip screen already
            knows it. */}
        <section className="mt-6 flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <FilterPanel rows={listed} applied={filters} onApply={setFilters} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search videos…"
              className="ml-auto w-64 rounded-field bg-ground px-3 py-1.5 text-sm
                border border-line placeholder:text-faint"
            />
          </div>
          <ActiveFilterChips filters={filters} onChange={setFilters} />
        </section>

        {/* ------------------------------------------------------- list */}
        <section className="mt-3 overflow-hidden rounded-panel border border-line">
          {/* `table-fixed` + explicit widths — testing, 18 Sep: "if the video
              name is very long it affects the other columns, and it shifts the
              last column buttons out of visible view."

              That is auto table layout doing exactly what it is specified to
              do: size every column to its content. One long name widened the
              Video column, squeezed the rest, and pushed Action off the edge.
              The `truncate` already on the name could not help — truncation
              needs a bounded width, and in an auto table there is none.

              `table-fixed` decides the columns from THESE widths and the first
              row only, so no cell can ever widen its column again. The name
              then truncates inside the space it is given, which is what the
              ellipsis was always for. Row layout is now identical whatever the
              data says. */}
          <table className="w-full table-fixed text-sm">
            <thead className="bg-card text-left text-xs font-extrabold
              uppercase tracking-[0.08em] text-muted">
              <tr>
                <th className={`${qa ? "w-[17%]" : "w-[24%]"} px-4 py-2`}>Video</th>
                <th className={`${qa ? "w-[11%]" : "w-[16%]"} px-4 py-2`}>Category</th>
                <th className="w-[7%] px-4 py-2">Length</th>
                {/* Tasks and sub-tasks were one column reading "2 · 71 sub",
                    so the sub-task count was a footnote on a heading that did
                    not mention it and could not be scanned down the page. */}
                <th className="w-[6%] px-4 py-2">Tasks</th>
                <th className="w-[8%] whitespace-nowrap px-4 py-2">Sub-tasks</th>
                {qa && (<>
                  <th className="w-[5%] px-2 py-2" title="Judge-approved, sent to the client (Accuracy page)">AI ok</th>
                  <th className="w-[5%] px-2 py-2" title="Sent to the crowd (Accuracy page)">Crowd</th>
                  <th className="w-[5%] px-2 py-2" title="(a) approved and right — Accuracy page">(a)</th>
                  <th className="w-[5%] px-2 py-2" title="(b) sent to the crowd — Accuracy page">(b)</th>
                </>)}
                <th className={`${qa ? "w-[10%]" : "w-[14%]"} px-4 py-2`}>Reviewed</th>
                <th className={`${qa ? "w-[10%]" : "w-[12%]"} whitespace-nowrap px-4 py-2`}>Status</th>
                {/* Action last and fixed: it is the only cell you click, so it
                    is the one that must never be the thing pushed off-screen. */}
                <th className={`${qa ? "w-[11%]" : "w-[13%]"} px-4 py-2`}>Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line2">
              {paged.map((j) => {
                const p = pill(j);
                const a = action(j);
                // Reviewed = decided on, either way.
                const reviewed = j.approved_count + j.rejected_count;
                return (
                  <tr key={j.video_id} className="bg-card hover:bg-ground">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Thumb poster={j.poster_url} media={j.media_url} />
                        <div className="min-w-0">
                          <div className="truncate text-ink">
                            {displayName(j.video_path)}
                          </div>
                          {/* 27 Oct 2026 (manager's review): no "—" placeholder
                              under the title. The session code shows only when a
                              video has one. */}
                          {j.session_code && (
                            <div className="text-[11px] text-muted">
                              {j.session_code}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted">
                      {categoryLabel(j.category, j.sub_category)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-muted">
                      {duration(j.duration_seconds)}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-muted">
                      {j.job_id === null ? "—" : j.task_count}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-muted">
                      {j.job_id === null ? "—" : j.subtask_count}
                    </td>
                    {qa && (() => {
                      const v = acc?.get(j.video_id);
                      const cell = "px-2 py-3 tabular-nums text-xs";
                      if (!v) return (<>{[0, 1, 2, 3].map((k) =>
                        <td key={k} className={`${cell} text-faint`}>—</td>)}</>);
                      return (<>
                        <td className={`${cell} text-verdict-approved-ink`}>{v.approved}</td>
                        <td className={`${cell} text-muted`}>{v.subtasks - v.approved}</td>
                        <td className={`${cell} font-bold ${v.a_pct != null && v.a_pct >= 95
                          ? "text-verdict-approved-ink" : "text-verdict-flagged-ink"}`}>{pct(v.a_pct)}</td>
                        <td className={`${cell} text-muted`}>{pct(v.b_pct)}</td>
                      </>);
                    })()}
                    <td className="px-4 py-3">
                      {j.job_id === null ? (
                        <span className="text-[11px] text-faint">—</span>
                      ) : (
                        <>
                          {/* THREE SEGMENTS, NOT ONE. The bar was approved-only,
                              so wpk_collect_packed_boxes_003 — 4 approved and 4
                              rejected, nothing left to do but submit — drew as
                              half empty and read 4/8. A reject is a decision;
                              green for approved, red for rejected, the track
                              itself for what is still pending. */}
                          <div className="flex h-1.5 w-24 overflow-hidden rounded-full bg-line">
                            <div className="h-full bg-verdict-approved"
                              style={{ width: `${(j.approved_count / j.subtask_count) * 100}%` }} />
                            <div className="h-full bg-verdict-rejected"
                              style={{ width: `${(j.rejected_count / j.subtask_count) * 100}%` }} />
                          </div>
                          <span className="text-[11px] text-faint">
                            {reviewed}/{j.subtask_count}
                            {reviewed === j.subtask_count && j.subtask_count > 0
                              && " completed"}
                            {j.flagged_count > 0 && (
                              <span className="ml-2 text-verdict-flagged-ink">
                                ▲ {j.flagged_count} flagged
                              </span>
                            )}
                          </span>
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {/* whitespace-nowrap: these labels are two and three
                          words, and the table's auto layout squeezes this
                          column until "AI pre-annotating…" wraps inside its
                          own pill. */}
                      <span className={`inline-flex whitespace-nowrap rounded-full
                        px-2 py-0.5 text-[11px] ring-1 ring-inset ${p.tone}`}>
                        {p.text}
                      </span>
                    </td>
                    {/* whitespace-nowrap: the action label is two words and the
                        table's auto layout will happily squeeze this column
                        until "Start review" wraps and spills past the row. */}
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      {a.to ? (
                        // min-w + centred: "Continue" is eight characters and
                        // "Start review" twelve, so padding alone made two
                        // buttons of visibly different width sit in one column.
                        // A fixed floor makes the column read as one control
                        // repeated, which is what it is.
                        // CARRIES THE PAGE. `a.to` is a bare "/job/229"; the
                        // queue's own query string goes with it so the video's
                        // Back button has something to come back to. See
                        // lib/queuereturn.ts.
                        <Link to={toVideo(a.to, location.search)}
                          className="inline-block min-w-[7.5rem] rounded-control bg-teal
                            px-3 py-1.5 text-center text-xs font-medium text-white
                            hover:bg-teal7">
                          {a.label}
                        </Link>
                      ) : (
                        <span className="text-xs text-faint">{a.label}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* Two different empties. An annotator who filters to nothing needs
              to clear the filter; one who arrives before the AI has finished
              needs to know the queue is not broken, and neither message helps
              in the other case. */}
          {/* PAGES. Hidden on a single page — a control that can only be
              pressed to stay where you are is noise.

              WINDOWED, NOT EVERY NUMBER. Seventy videos is seven pages and all
              seven fit; seven hundred would be seventy buttons and a wrapped
              row. First and last are always reachable, the current page keeps
              two neighbours either side, and the gaps become an ellipsis — so
              the control's width stops growing while the list keeps getting
              longer. */}
          {pageCount > 1 && (
            <nav
              aria-label="Pages"
              className="flex flex-wrap items-center justify-center gap-1 border-t
                border-line2 px-4 py-3 text-sm"
            >
              <button
                onClick={() => setPage(current - 1)}
                disabled={current === 1}
                className="rounded-control border border-line bg-card px-2.5 py-1
                  text-text2 hover:bg-ground disabled:opacity-40"
              >Prev</button>

              {pageNumbers(current, pageCount).map((n, i) =>
                n === null ? (
                  <span key={`gap${i}`} className="px-1 text-muted">…</span>
                ) : (
                  <button
                    key={n}
                    onClick={() => setPage(n)}
                    aria-current={n === current ? "page" : undefined}
                    className={`min-w-8 rounded-control border px-2.5 py-1 ${
                      n === current
                        ? "border-teal bg-teal text-white"
                        : "border-line bg-card text-text2 hover:bg-ground"
                    }`}
                  >{n}</button>
                ))}

              <button
                onClick={() => setPage(current + 1)}
                disabled={current === pageCount}
                className="rounded-control border border-line bg-card px-2.5 py-1
                  text-text2 hover:bg-ground disabled:opacity-40"
              >Next</button>

              {/* What the pages are OF. Without it, filtering to 12 rows and
                  seeing "1 2" gives no sense of how much was cut away. */}
              <span className="ml-3 text-xs text-muted">
                {(current - 1) * PER_PAGE + 1}–
                {Math.min(current * PER_PAGE, visible.length)} of {visible.length}
              </span>
            </nav>
          )}

          {!visible.length && (listed.length ? (
            <Empty
              title="Nothing matches that."
              hint="Try clearing the search box, or pick the 'All' chip to see every video ready for review."
            />
          ) : (
            <Empty
              title="No videos are ready yet."
              hint={`The AI is still working through the library — ${stats.notStarted} `
                + "of them. Videos appear here as soon as their annotations land."}
            />
          ))}
        </section>
      </div>

      {/* 6.3.5: informational mode — guidance, no live pass/fail. */}
      <ChecklistDrawer
        open={guidelines}
        informational
        onClose={() => setGuidelines(false)}
        /* The six automatic checks, worded as Video_Annotation_Tool_v1.6 words
           them. They were paraphrased before, so the guidelines a customer
           reads and the prototype they signed off on did not match. */
        checks={[
          { id: "resolved", blocking: true, pass: false, count: 0,
            label: "Every sub-task approved or rejected" },
          { id: "rejects", blocking: true, pass: false, count: 0,
            label: "No rejected sub-tasks left in the video" },
          { id: "tiling", blocking: true, pass: false, count: 0,
            label: "No gaps or overlaps — sub-tasks tile each task exactly" },
          { id: "hands", blocking: true, pass: false, count: 0,
            label: "Every sub-task names a hand (or is idle)" },
          { id: "blacklist", blocking: true, pass: false, count: 0,
            label: "No blacklisted words in descriptions" },
          { id: "length", blocking: true, pass: false, count: 0,
            label: "Descriptions ≥ 5 words, present tense" },
        ]}
        manualLabels={[
          "No faces or personal info in retained footage",
          "Object names follow the customer glossary",
        ]}
        manual={[false, false]}
        onToggleManual={() => {}}
        onSubmit={() => {}}
        canSubmit={false}
      />

    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-panel bg-card shadow-card px-4 py-3 border border-line">
      <Eyebrow>{label}</Eyebrow>
      <div className="mt-1 text-xl font-semibold text-ink">{value}</div>
      {hint && <div className="text-[11px] text-faint">{hint}</div>}
    </div>
  );
}


